/**
 * The shared-recipes wire protocol (PLAN-COLLAB B3).
 *
 * Shared by the Worker (which writes these responses) and the browser (which
 * reads them), in the style of `scoresheets/protocol.ts` and
 * `share/protocol.ts`. One module, so the two halves cannot drift about what a
 * member, an invite or a refusal is.
 *
 * ```
 * session  GET    /api/shared-recipes/:id/sync             → WebSocket to the room
 * session  GET    /api/shared-recipes/:id/pull             → { ok, state, version }
 * session  GET    /api/shared-recipes/:id/members          → { ok, members, invites }
 * session  POST   /api/shared-recipes/:id/invites          → { ok, invite }
 * session  DELETE /api/shared-recipes/:id/invites/:token   → { ok }
 * session  DELETE /api/shared-recipes/:id/members/:userId  → { ok }
 * session  DELETE /api/shared-recipes/:id                  → { ok }
 * session  GET    /api/shared-recipes/:id/history          → { ok, head, entries }
 * session  GET    /api/shared-recipes/:id/history/:v       → { ok, version, recipe }
 * session  GET    /api/invites/:token                      → { ok, recipeName, ownerName, email, state }
 * session  POST   /api/invites/:token/accept               → { ok, recipeId }
 * ```
 *
 * Every route is behind the session. The invite routes are too, even though
 * the person opening one may have no account yet: D-CO6 binds an invite to an
 * address, and the only way to know who is asking is a session. Somebody
 * without one signs up first and comes back.
 *
 * ## One 404 for everyone outside
 *
 * A recipe that does not exist, a room you were removed from and a room you
 * were never in all answer `404 { ok: false, error: 'not_found' }`, the same
 * bytes (constraint 9). A path's recipe id is a filter checked against D1
 * membership and never a lookup, so a guess at somebody else's id learns
 * nothing it could not learn from a guess at a made-up one.
 */

import type { RecipeDoc, WorkspaceState } from '@beerwright/commands';
import type { Plan } from '../lib/plan';

/**
 * The close code for "this room does not answer to you": never made, wiped,
 * or you were removed. One code for all three, because the client does the
 * same thing for each, and constraint 9 wants them indistinguishable anyway.
 *
 * Here rather than beside the room object because both ends read it: the room
 * closes with it, and the browser's `SyncClient` stops for good on it, and the
 * browser cannot import Worker code.
 */
export const ROOM_CLOSED_CODE = 4404;

/**
 * The close code for "this room is being deleted": its row is marked closing
 * and `closeRoom` has not finished. Not {@link ROOM_CLOSED_CODE}, because the
 * client erases a room's queue and snapshot on that one, and a close that
 * crashed half-way leaves a room that still exists, with members whose queued
 * edits must survive until the retry either finishes the close (and each of
 * them gets a copy through their workspace) or the room goes on. So the client
 * treats this as any dropped socket: it backs off and tries again.
 */
export const ROOM_CLOSING_CODE = 4409;

/** Where a room's routes live. */
export const SHARED_RECIPES_BASE_PATH = '/api/shared-recipes';

/** Where an invite's own two routes live, keyed by its token rather than a recipe. */
export const INVITES_BASE_PATH = '/api/invites';

/** The room socket's path for one recipe. */
export function sharedRecipeSyncPath(recipeId: string): string {
  return `${SHARED_RECIPES_BASE_PATH}/${encodeURIComponent(recipeId)}/sync`;
}

/**
 * Whether a request path is a room socket's.
 *
 * The session middleware asks, so that a dead session's upgrade to a room is
 * answered the way one to `/api/sync` is: a completed handshake saying
 * `unauthorized`, which a browser can read, rather than a 401 it cannot.
 */
export function isSharedRecipeSyncPath(pathname: string): boolean {
  if (!pathname.startsWith(`${SHARED_RECIPES_BASE_PATH}/`)) return false;
  const rest = pathname.slice(SHARED_RECIPES_BASE_PATH.length + 1).split('/');
  return rest.length === 2 && rest[0] !== '' && rest[1] === 'sync';
}

export function sharedRecipePullPath(recipeId: string): string {
  return `${SHARED_RECIPES_BASE_PATH}/${encodeURIComponent(recipeId)}/pull`;
}

export function sharedRecipeMembersPath(recipeId: string): string {
  return `${SHARED_RECIPES_BASE_PATH}/${encodeURIComponent(recipeId)}/members`;
}

export function sharedRecipeMemberPath(recipeId: string, userId: string): string {
  return `${sharedRecipeMembersPath(recipeId)}/${encodeURIComponent(userId)}`;
}

export function sharedRecipeInvitesPath(recipeId: string): string {
  return `${SHARED_RECIPES_BASE_PATH}/${encodeURIComponent(recipeId)}/invites`;
}

export function sharedRecipeInvitePath(recipeId: string, token: string): string {
  return `${sharedRecipeInvitesPath(recipeId)}/${encodeURIComponent(token)}`;
}

export function sharedRecipePath(recipeId: string): string {
  return `${SHARED_RECIPES_BASE_PATH}/${encodeURIComponent(recipeId)}`;
}

export function sharedRecipeHistoryPath(recipeId: string): string {
  return `${SHARED_RECIPES_BASE_PATH}/${encodeURIComponent(recipeId)}/history`;
}

export function sharedRecipeVersionPath(recipeId: string, version: number): string {
  return `${sharedRecipeHistoryPath(recipeId)}/${String(version)}`;
}

export function invitePath(token: string): string {
  return `${INVITES_BASE_PATH}/${encodeURIComponent(token)}`;
}

export function inviteAcceptPath(token: string): string {
  return `${invitePath(token)}/accept`;
}

/** The page an invite email links to. The SPA route, not an API path. */
export function inviteAppPath(token: string): string {
  return `/invite/${encodeURIComponent(token)}`;
}

/**
 * Whether this plan may invite anybody (D-CO4): any paid tier.
 *
 * The one predicate, so the Worker's refusal and whatever the client shows
 * beside the invite button cannot disagree about which tier it is. An owner
 * who lapses keeps the room working; only a new invite needs a paid plan.
 */
export function planAllowsInvites(plan: Plan): boolean {
  return plan !== 'trial';
}

/**
 * Editors a room may hold besides its owner, open invites included (D-CO5).
 *
 * Here rather than only beside the D1 queries because both ends read it: the
 * Worker refuses the sixth seat, and the invite dialog stops offering the
 * field once five are taken, so the owner is told before they type an address
 * rather than after.
 */
export const MAX_CO_BREWERS = 5;

export type SharedRecipeRole = 'owner' | 'editor';

/**
 * One person in a room, as the roster shows them.
 *
 * `email` is the owner's to see and nobody else's: the owner typed every
 * address into an invite, and an editor has no need of the others'. For
 * anybody but the owner it is `null`.
 */
export interface SharedRecipeMember {
  readonly userId: string;
  readonly role: SharedRecipeRole;
  readonly name: string;
  readonly email: string | null;
  /** Milliseconds. */
  readonly joinedAt: number;
}

/** An invite nobody has answered yet. Only the owner is shown these. */
export interface SharedRecipeInvite {
  readonly token: string;
  readonly email: string;
  /** Milliseconds. */
  readonly createdAt: number;
}

/** `GET …/pull`: the room's whole state and its head. */
export interface SharedRecipePullResponse {
  readonly ok: true;
  readonly state: WorkspaceState;
  readonly version: number;
}

/** `GET …/members`. `invites` is empty for anybody but the owner. */
export interface SharedRecipeMembersResponse {
  readonly ok: true;
  readonly members: readonly SharedRecipeMember[];
  readonly invites: readonly SharedRecipeInvite[];
}

/** `POST …/invites`. */
export interface SharedRecipeInviteResponse {
  readonly ok: true;
  readonly invite: SharedRecipeInvite;
}

/** `GET …/history/:v`. The timeline itself is `history/protocol.ts`' `RecipeHistoryResponse`. */
export interface SharedRecipeVersionResponse {
  readonly ok: true;
  readonly version: number;
  readonly recipe: RecipeDoc;
}

/** Where an invite stands. A revoked invite reads as `expired`: to its holder they are one thing. */
export type InviteState = 'open' | 'accepted' | 'expired';

/** `GET /api/invites/:token`: what the invite page shows, and nothing else of the recipe (D-CO6). */
export interface InviteSummaryResponse {
  readonly ok: true;
  readonly recipeName: string;
  readonly ownerName: string;
  /** The address the invite was sent to, so a wrong-account visitor can be told which. */
  readonly email: string;
  readonly state: InviteState;
}

/** `POST /api/invites/:token/accept`. */
export interface InviteAcceptResponse {
  readonly ok: true;
  readonly recipeId: string;
}

/**
 * Every refusal these routes make.
 *
 * - `not_found` (404): not a member, or no such thing (constraint 9).
 * - `invalid_request` (400): a body or path segment that does not parse.
 * - `plan_required` (403): a trial account inviting (D-CO4).
 * - `editor_limit` (409): the room's seats are taken, open invites included.
 * - `already_member` (409): that address, or that account, is in the room.
 * - `self_invite` (400): the owner's own address.
 * - `owner_cannot_leave` (400): the owner deletes for everyone instead.
 * - `wrong_address` (403): signed in as somebody else; `email` says who was asked.
 * - `invite_closed` (410): accepted, revoked or past its fourteen days.
 * - `closing` (409): the room is being deleted.
 * - `recipe_conflict` (409): the invitee already holds a recipe under the
 *   shared recipe's id, so the reference cannot be added
 *   ({@link RECIPE_CONFLICT_MESSAGE}).
 * - `recipe_id_taken` (409): the caller holds this recipe, but another
 *   account's room already has its id ({@link RECIPE_ID_TAKEN_MESSAGE}).
 * - `invite_limit` (429): too many invite emails in a rolling day
 *   ({@link INVITE_LIMIT_MESSAGE}).
 */
export type SharedRecipeError =
  | 'not_found'
  | 'invalid_request'
  | 'plan_required'
  | 'editor_limit'
  | 'already_member'
  | 'self_invite'
  | 'owner_cannot_leave'
  | 'wrong_address'
  | 'invite_closed'
  | 'closing'
  | 'recipe_conflict'
  | 'recipe_id_taken'
  | 'invite_limit';

export type SharedRecipeErrorResponse =
  | { readonly ok: false; readonly error: Exclude<SharedRecipeError, 'wrong_address'> }
  | { readonly ok: false; readonly error: 'wrong_address'; readonly email: string };

/**
 * What the client shows for `recipe_id_taken`.
 *
 * Recipe ids are unique per workspace, and a room is named by one, so a
 * recipe whose id another account's room already holds cannot be shared as
 * it is. A duplicate has a fresh id and can.
 */
export const RECIPE_ID_TAKEN_MESSAGE =
  "This recipe can't be shared as it is. Duplicate it and share the copy.";

/** What the client shows for `recipe_conflict`, on accepting. */
export const RECIPE_CONFLICT_MESSAGE =
  "You already have a recipe with this one's id, probably an imported copy. Delete or duplicate yours, then accept again.";

/** What the client shows for `invite_limit`. */
export const INVITE_LIMIT_MESSAGE = "You've sent a lot of invites today. Try again tomorrow.";

/**
 * The name shown for a member who has not set one.
 *
 * Never their address: a roster, and the presence the room broadcasts, are
 * read by everybody in the room, and an address is not something an editor
 * agreed to show the others. The invite email and the invite page are the
 * exception, and use the owner's address instead, because the invitee has to
 * recognise who is asking.
 */
export const UNNAMED_MEMBER = 'Co-brewer';
